/**
 * API 错误处理 · 友好文案 + 全局 pub/sub bus.
 *
 * 设计:
 *   - humanizeApiError(e) · 任意 unknown → 一句话中文 (UI toast 用)
 *   - emitApiError(e, context?) · axios interceptor 调用 · 不直接绑 React
 *   - onApiError(fn) · 订阅 · 通常由 <GlobalApiErrorToast /> 一处订阅
 *
 * 分支:
 *   ApiError.status=0    → 网络/上游不可达
 *   ApiError.status=401  → 未登录 / 登录过期
 *   ApiError.status=403  → 无权
 *   ApiError.status>=500 → 服务端故障
 *   ApiError 其他/普通 Error → 原始 message
 *   非 Error → fallback
 *
 * 静默 toast:
 *   apiClient.get(url, { _silentError: true })  // 页面自己处理 · 不全局 toast
 */
import { ApiError, CAD_PREVIEW_PENDING_CODE } from "@/api/client";
export {
  _clearApiErrorListeners,
  emitApiError,
  onApiError,
} from "./api-error-bus";
export type { ApiErrorEvent } from "./api-error-bus";

/** 上传时后端认出「系统读不了」的文件给的错误码(3D:cad_import · PDF:security/unreadable_files.py)。 */
export const UNREADABLE_FILE_CODES: ReadonlySet<string> = new Set([
  "CAD_ENCRYPTED",
  "CAD_NOT_VALID_STEP",
  "DOCUMENT_ENCRYPTED",
  "DOCUMENT_NOT_VALID_PDF",
  "DOCUMENT_PASSWORD_PROTECTED",
]);

export function humanizeApiError(e: unknown, fallback = "操作失败"): string {
  if (e instanceof ApiError) {
    if (e.status === 0) return "服务暂不可达, 请检查网络或稍后重试";
    // 大件 CAD 后台处理中 (202) · 不是失败 · 引导稍候(下载按钮等一次性动作命中)
    if (e.code === CAD_PREVIEW_PENDING_CODE)
      return "大件后台处理中 · 请稍后再试(完成后自动可用)";
    // 文件读不了(DLP 加密 / 设了打开口令 / 内容不是这种格式):后端 detail.message 是说清楚
    // 怎么办的中文,直接给人看(别带 [CODE] 前缀)
    if (e.code && UNREADABLE_FILE_CODES.has(e.code)) {
      const detail = e.detail;
      if (
        detail &&
        typeof detail === "object" &&
        typeof (detail as { message?: unknown }).message === "string"
      )
        return (detail as { message: string }).message;
    }
    // V70 (2026-05-25) · 401 不只是 token 过期 · 业务 code 区分文案
    // 登录密码错 = INVALID_CREDENTIALS · 之前一刀切返"未登录或登录已过期" 误导用户
    // 2026-10-29 · 注册必须凭开通邀请码(400 SIGNUP_INVITE_INVALID · 后端 c177)
    if (e.code === "SIGNUP_INVITE_INVALID")
      return "邀请码无效、已过期或已被使用 · 请向智擎团队索取新的邀请码";
    if (e.code === "INVALID_CREDENTIALS")
      return "账号或密码错误 · 请检查后重试";
    // 2026-10-29 · 注册改成手机号必填、邮箱选填(c178)· 两者都全局唯一
    if (e.code === "PHONE_ALREADY_REGISTERED")
      return "这个手机号已经注册过 · 请直接登录";
    if (e.code === "EMAIL_ALREADY_REGISTERED")
      return "这个邮箱已经注册过 · 请直接登录,或换一个邮箱";
    if (e.code === "ACCOUNT_DISABLED") return "账号已停用 · 请联系系统管理员";
    if (e.code === "ACCOUNT_LOCKED")
      return "账号已锁定 · 多次密码错误触发 · 请联系管理员解锁";
    // 2026-09-24 · 后端登录失败锁定(429 LOGIN_LOCKED · 15 分钟窗口到期自动解锁)。
    // 必须在下面 429 通用分支之前:那条是驾驶舱轮询的「已自动降速」文案, 放在登录页会误导。
    if (e.code === "LOGIN_LOCKED")
      return "登录失败次数过多 · 请约 15 分钟后再试";
    if (e.code === "SOURCE_UPLOAD_BUSY")
      return "上传正在处理其他资料，请稍后重试；本次资料未导入";
    if (e.code === "SOURCE_BUNDLE_TOO_LARGE")
      return "本次资料总大小超过上传限制，资料未导入；请减少文件后重试";
    if (e.code === "SECURITY_SCAN_UNAVAILABLE")
      return "病毒扫描暂不可用，资料未导入；请稍后重试";
    if (e.code === "SOURCE_TEMPORARY_STORAGE_UNAVAILABLE")
      return "上传临时空间不足，资料未导入；请稍后重试或联系管理员";
    if (e.code === "DUPLICATE_PART")
      return "已有零件使用这些资料；本次尚未新增，请查看已有档案或明确另建原因";
    if (e.code === "REVISION_CONFLICT")
      return "已有零件的工程内容不同；请核对差异并明确版本变更原因";
    if (e.code === "CURRENT_REVISION_CHANGED")
      return "当前工程版本已变化；请重新核对最新版本，输入已保留";
    if (e.code === "DUPLICATE_PROJECT_DECISION_REQUIRED")
      return "已有项目包含相同资料；请明确复用已有项目或继续新建，并核对原因";
    if (e.status === 401) return "未登录或登录已过期 · 请重新登录";
    if (e.status === 403) return "当前账号无此页权限 · 请联系系统管理员开通";
    // 限流不是故障:驾驶舱轮询带退避,横幅只需说明「稍后自动恢复」而不是甩原始 HTTP 文案
    if (e.status === 429) return "请求过于频繁 · 已自动降速，稍后自动恢复";
    if (e.status >= 500)
      return `服务端暂时故障 (HTTP ${e.status}) · 请稍后重试`;
    return e.message;
  }
  return e instanceof Error ? e.message : fallback;
}

export { ApiError } from "@/api/client";
